import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, effect } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { MenuModule } from 'primeng/menu';
import { RadioButtonModule } from 'primeng/radiobutton';
import { USER_ROLE } from 'src/app/@core/models/enums';
import { UI_FEATURE } from 'src/app/@core/models/feature-mapping';
import { IUser, SHARE_ACCESS } from 'src/app/@core/models/model';
import { AuthenticationService } from 'src/app/@core/services/authentication.service';
import { BrowserExtensionService } from 'src/app/@core/services/browser-extension.service';
import { FeatureFlagService } from 'src/app/@core/services/feature-flag.service';
import { CONNECTOR_PROVIDER_INFO, CONNECTOR_PROVIDERS, DATABASE_PROVIDERS, MISC_CONNECTORS, TOOLKIT_PROVIDERS } from 'src/app/features/general-agent/constants/tool.constants';
import { GeneralAgentService } from 'src/app/features/general-agent/services/general-agent.service';
import { CheckboxComponent } from '../../../@core/components/ui/checkbox/checkbox.component';
import { DialogComponent } from '../../../@core/components/ui/dialog/dialog.component';
import { PopoverComponent } from '../../../@core/components/ui/popover/popover.component';
import { ToggleComponent } from '../../../@core/components/ui/toggle/toggle.component';
import { ToastService } from '../../../@core/services/toast.service';
import { INTEGRATION_STATUS } from '../../../@shared/components/badge/badge.enums';
import { AssetUrlPipe } from '../../../@shared/pipes/asset-url.pipe';
import { SanitizeHtmlPipe } from '../../../@shared/pipes/sanitizeHtml.pipe';
import { SIGNATORY_TYPE } from '../../models/contract-management.model';
import { INTEGRATION_TYPE } from '../../models/integration-types.enum';
import { ContractSignatoryService } from '../../services/contract-signatory.service';
import { IntegrationsService } from '../../services/integrations.service';
import { MicrosoftAzureService } from '../../services/microsoft-azure.service';
import { AdobeSignComponent } from '../adobe-sign/adobe-sign.component';
import { DocuSignComponent } from '../docu-sign/docu-sign.component';
import { MicrosoftAzureComponent } from '../microsoft-azure/microsoft-azure.component';
import { DefaultIntegrationDialogComponent } from './components/default-integration-dialog/default-integration-dialog.component';
@Component({
  selector: 'app-integrations',
  standalone: true,
  imports: [
    DialogComponent,
    ButtonModule,
    MenuModule,
    RadioButtonModule,
    FormsModule,
    CommonModule,
    ToggleComponent,
    PopoverComponent,
    CheckboxComponent,
    DocuSignComponent,
    AdobeSignComponent,
    MicrosoftAzureComponent,
    DefaultIntegrationDialogComponent,
    DefaultIntegrationDialogComponent,
    SanitizeHtmlPipe,
    AssetUrlPipe
  ],
  templateUrl: './integrations.component.html',
  styleUrl: './integrations.component.scss'
})
export class IntegrationsComponent {


  currentUser!: IUser;
  isAdmin: boolean = false;
  isLoading: boolean = false;

  constructor(
    private router: Router,
    private contractSignatoryService: ContractSignatoryService,
    private toast: ToastService,
    private microsoftAzureService: MicrosoftAzureService,
    private integrationService: IntegrationsService,
    private route: ActivatedRoute,
    private generalAgentService: GeneralAgentService,
    private browserExtensionService: BrowserExtensionService,
    private authenticationService: AuthenticationService,
    private cdr: ChangeDetectorRef,
    private features: FeatureFlagService
  ) {

    this.authenticationService.currentUser.subscribe(res => {
      this.currentUser = res as IUser;
    });

    this.route.queryParams.subscribe(params => {
      // status=success&connectedAccountId=ca_uM15Em3Q21mN&appName=gmail
      // share_point?status=success&connected_account_id=ca_RSF8nn70ARE_
      const status = params['status'] || '';
      const connectedAccountId = params['connectedAccountId'] || params['connected_account_id']
      const connectorToolkit = this.route.snapshot.firstChild?.params['connectorToolkit'] || this.route.snapshot.paramMap.get('connectorToolkit') || ''
      const appName = params['appName'] || connectorToolkit;
      if (status === 'success' && connectedAccountId && appName && connectorToolkit) {
        this.integrationService.addConnector({
          toolKit: connectorToolkit,
          connectedAccountId: connectedAccountId,
          status: status,
          appName: appName ? appName : connectorToolkit
        }).subscribe((res: any) => {
          if (res.data.status) {
            this.toast.success('Connection established successfully');
            this.router.navigate(['/settings/integrations']);
          } else {
            this.toast.error('Connection failed', 'Connection failed');
            this.router.navigate(['/settings/integrations']);
          }
        });
      }

    });

    effect(() => {
      const isExtensionInstalled = this.browserExtensionService.isExtensionInstalled();
      this.integrationCategories = this.integrationCategories.map(category => {
        if (category.key === 'BROWSER_INTEGRATION_SETTINGS') {
          return {
            ...category,
            items: category.items.map(item => item.value === MISC_CONNECTORS.MY_BROWSER ? { ...item, connected: isExtensionInstalled } : item)
          }
        }
        return category;
      });
    });
  }

  public signatories = [
    {
      name: 'Docusign',
      id: SIGNATORY_TYPE.DOCU_SIGN
    },
    {
      name: 'Adobe sign',
      id: SIGNATORY_TYPE.ADOBE_SIGN
    }
  ]

  INTEGRATION_STATUS = INTEGRATION_STATUS
  public selectedSignatory!: SIGNATORY_TYPE
  public selectedDocumentProcessing!: string

  SIGNATORY_TYPE = SIGNATORY_TYPE
  showIntegrationDialog: boolean = false;
  currentIntegrationType: SIGNATORY_TYPE | string | null = null;
  currentIntegrationData: any = null;
  integrationDialogConfig: {
    key: string;
    value: any;
    title: string;
    width: string;
    height: string;
  } = {
      key: '',
      value: null,
      title: '',
      width: '36rem',
      height: '40rem'
    };

  /**
   * Search text for filtering integrations.
   */
  public searchText = '';

  /** Integration types shown for BASIC plan (Signatures, SSO, Document processing only). */
  private static readonly BASIC_INTEGRATION_TYPES = [
    INTEGRATION_TYPE.SIGNATORY_SETTINGS,
    INTEGRATION_TYPE.SSO_SETTINGS,
    INTEGRATION_TYPE.DOCUMENT_PROCESSING_SETTINGS
  ] as const;

  /** Full list of filter options (used when premium is present). */
  private readonly allFilterOptions = [
    { label: 'Signatures', value: INTEGRATION_TYPE.SIGNATORY_SETTINGS, checked: true },
    { label: 'SSO', value: INTEGRATION_TYPE.SSO_SETTINGS, checked: true },
    { label: 'Document processing', value: INTEGRATION_TYPE.DOCUMENT_PROCESSING_SETTINGS, checked: true },
    { label: 'Mails', value: INTEGRATION_TYPE.EMAIL_INTEGRATION_SETTINGS, checked: true },
    { label: 'Meetings', value: INTEGRATION_TYPE.MEETING_INTEGRATION_SETTINGS, checked: true },
    { label: 'Calendar', value: INTEGRATION_TYPE.CALENDAR_INTEGRATION_SETTINGS, checked: true },
    { label: 'Sheets', value: INTEGRATION_TYPE.SHEETS_INTEGRATION_SETTINGS, checked: true },
    { label: 'Docs', value: INTEGRATION_TYPE.DOCS_INTEGRATION_SETTINGS, checked: true },
    { label: 'Slides', value: INTEGRATION_TYPE.SLIDES_INTEGRATION_SETTINGS, checked: true },
    { label: 'Tasks', value: INTEGRATION_TYPE.TASKS_INTEGRATION_SETTINGS, checked: true },
    { label: 'Storage', value: INTEGRATION_TYPE.STORAGE_INTEGRATION_SETTINGS, checked: true },
    { label: 'Databases', value: INTEGRATION_TYPE.DATABASE_INTEGRATION_SETTINGS, checked: true },
    { label: 'Browser', value: INTEGRATION_TYPE.BROWSER_INTEGRATION_SETTINGS, checked: true }
  ];

  private static readonly MEMBER_HIDDEN_FILTER_TYPES = [
    INTEGRATION_TYPE.DATABASE_INTEGRATION_SETTINGS,
    INTEGRATION_TYPE.DOCUMENT_PROCESSING_SETTINGS
  ] as const;

  public get filterOptions(): { label: string; value: string; checked: boolean }[] {
    const hasPremium = this.features.checkFeature(UI_FEATURE.INTEGRATIONS_PREMIUM).allowed;
    const hasBasic = this.features.checkFeature(UI_FEATURE.INTEGRATIONS_BASIC).allowed;
    let options: { label: string; value: string; checked: boolean }[];
    if (hasPremium) {
      options = this.allFilterOptions;
    } else if (hasBasic) {
      options = this.allFilterOptions.filter(opt =>
        IntegrationsComponent.BASIC_INTEGRATION_TYPES.includes(opt.value as any)
      );
    } else {
      return [];
    }
    const isAdmin = this.currentUser && [USER_ROLE.ADMIN, USER_ROLE.SUPER_ADMIN, SHARE_ACCESS.ORG_ADMIN].includes(this.currentUser.role);
    if (!isAdmin) {
      options = options.filter(opt => !IntegrationsComponent.MEMBER_HIDDEN_FILTER_TYPES.includes(opt.value as any));
    }
    return options;
  }


  public integrationCategories: {
    key: string;
    title: string;
    hasToggle: boolean;
    autoToggle?: boolean;
    selectedKey: string | null;
    isAdminOnly?: boolean;
    items: {
      id: number;
      name: string;
      value: string;
      description: string;
      imgPath?: string;
      imgSvgPath?: string;
      route: string;
      connected?: boolean;
      comingSoon?: boolean;
      data?: any;
      isAdminOnly?: boolean;
    }[];

    showChevron: boolean;
  }[] = [
      {
        key: INTEGRATION_TYPE.SIGNATORY_SETTINGS,
        title: 'Signatures',
        hasToggle: false,
        autoToggle: true,
        selectedKey: 'selectedSignatory',
        isAdminOnly: true,
        items: [{
          id: 1,
          name: 'Docusign',
          value: SIGNATORY_TYPE.DOCU_SIGN,
          description: "Accelerate approvals and Signatures with Docusign electronic document signing",
          imgPath: 'images/logos/docusign.png',
          route: '/settings/integrations/docu-sign',
          connected: false
        },
        {
          id: 2,
          name: 'Adobe sign',
          value: SIGNATORY_TYPE.ADOBE_SIGN,
          description: "Accelerate approvals and Signatures with Adobe's electronic document signing",
          imgPath: 'images/logos/adobe.png',
          route: '/settings/integrations/adobe-sign',
          connected: false
        }],
        showChevron: true
      },
      {
        key: INTEGRATION_TYPE.SSO_SETTINGS,
        title: 'SSO',
        hasToggle: false,
        selectedKey: null,
        isAdminOnly: true,
        items: [
          {
            id: 3,
            name: 'Microsoft Azure',
            value: 'MICROSOFT_AZURE',
            description: "Seamless SSO authentication with Microsoft Azure Active Directory",
            imgPath: 'images/logos/microsoft-azure.png',
            route: '/settings/integrations/microsoft-azure',
            connected: false,
          },
          {
            id: 4,
            name: 'Google',
            value: 'GOOGLE',
            description: "Seamless SSO authentication with Google Workspace",
            imgPath: 'images/logos/google.png',
            route: '',
            connected: false,
            comingSoon: true,
          }
        ],
        showChevron: true
      },
      {
        key: INTEGRATION_TYPE.DOCUMENT_PROCESSING_SETTINGS,
        title: 'Document Processing',
        hasToggle: true,
        autoToggle: true,
        isAdminOnly: true,
        selectedKey: 'selectedDocumentProcessing',
        items: [
          {
            id: 1,
            name: "Default",
            value: "DEFAULT",
            description: "Default document processing",
            imgPath: "images/logos/default-ocr.png",
            route: "",
            connected: false
          },
          {
            id: 2,
            name: "Premium OCR",
            value: "PREMIUM",
            description: "Enhanced document processing with advanced OCR",
            imgPath: "images/logos/default-ocr.png",
            route: "",
            connected: false
          }
        ],
        showChevron: false
      },
      {
        key: INTEGRATION_TYPE.EMAIL_INTEGRATION_SETTINGS,
        title: 'Mails',
        hasToggle: false,
        selectedKey: null,
        items: [
          {
            id: 1,
            name: CONNECTOR_PROVIDER_INFO[TOOLKIT_PROVIDERS.GMAIL].name,
            value: TOOLKIT_PROVIDERS.GMAIL,
            description: 'Connect your Gmail account for seamless email integration',
            imgSvgPath: CONNECTOR_PROVIDER_INFO[TOOLKIT_PROVIDERS.GMAIL].icon,
            route: '',
            connected: false
          },
          {
            id: 2,
            name: CONNECTOR_PROVIDER_INFO[TOOLKIT_PROVIDERS.OUTLOOK].name,
            value: TOOLKIT_PROVIDERS.OUTLOOK,
            description: 'Connect your Outlook account for seamless email integration',
            imgSvgPath: CONNECTOR_PROVIDER_INFO[TOOLKIT_PROVIDERS.OUTLOOK].icon,
            route: '',
            connected: false
          }
        ],
        showChevron: true
      },
      {
        key: INTEGRATION_TYPE.MEETING_INTEGRATION_SETTINGS,
        title: 'Meetings',
        hasToggle: false,
        selectedKey: null,
        items: [
          {
            id: 1,
            name: CONNECTOR_PROVIDER_INFO[TOOLKIT_PROVIDERS.GOOGLE_MEET].name,
            value: TOOLKIT_PROVIDERS.GOOGLE_MEET,
            description: 'Schedule and manage meetings with Google Meet',
            imgSvgPath: CONNECTOR_PROVIDER_INFO[TOOLKIT_PROVIDERS.GOOGLE_MEET].icon,
            route: '',
            connected: false
          }
        ],
        showChevron: true
      },
      {
        key: INTEGRATION_TYPE.CALENDAR_INTEGRATION_SETTINGS,
        title: 'Calendar',
        hasToggle: false,
        selectedKey: null,
        items: [
          {
            id: 1,
            name: CONNECTOR_PROVIDER_INFO[TOOLKIT_PROVIDERS.GOOGLE_CALENDAR].name,
            value: TOOLKIT_PROVIDERS.GOOGLE_CALENDAR,
            description: 'Sync your calendar with Google Calendar',
            imgSvgPath: CONNECTOR_PROVIDER_INFO[TOOLKIT_PROVIDERS.GOOGLE_CALENDAR].icon,
            route: '',
            connected: false
          }
        ],
        showChevron: true
      },
      {
        key: INTEGRATION_TYPE.SHEETS_INTEGRATION_SETTINGS,
        title: 'Sheets',
        hasToggle: false,
        selectedKey: null,
        items: [
          {
            id: 1,
            name: CONNECTOR_PROVIDER_INFO[TOOLKIT_PROVIDERS.GOOGLE_SHEETS].name,
            value: TOOLKIT_PROVIDERS.GOOGLE_SHEETS,
            description: 'Work with spreadsheets using Google Sheets',
            imgSvgPath: CONNECTOR_PROVIDER_INFO[TOOLKIT_PROVIDERS.GOOGLE_SHEETS].icon,
            route: '',
            connected: false
          },
          {
            id: 2,
            name: CONNECTOR_PROVIDER_INFO[TOOLKIT_PROVIDERS.MICROSOFT_EXCEL].name,
            value: TOOLKIT_PROVIDERS.MICROSOFT_EXCEL,
            description: 'Work with spreadsheets using Microsoft Excel',
            imgSvgPath: CONNECTOR_PROVIDER_INFO[TOOLKIT_PROVIDERS.MICROSOFT_EXCEL].icon,
            route: '',
            connected: false
          }
        ],
        showChevron: true
      },
      {
        key: INTEGRATION_TYPE.DOCS_INTEGRATION_SETTINGS,
        title: 'Docs',
        hasToggle: false,
        selectedKey: null,
        items: [
          {
            id: 1,
            name: CONNECTOR_PROVIDER_INFO[TOOLKIT_PROVIDERS.GOOGLE_DOCS].name,
            value: TOOLKIT_PROVIDERS.GOOGLE_DOCS,
            description: 'Create and edit documents with Google Docs',
            imgSvgPath: CONNECTOR_PROVIDER_INFO[TOOLKIT_PROVIDERS.GOOGLE_DOCS].icon,
            route: '',
            connected: false
          }
        ],
        showChevron: true
      },
      {
        key: INTEGRATION_TYPE.SLIDES_INTEGRATION_SETTINGS,
        title: 'Slides',
        hasToggle: false,
        selectedKey: null,
        items: [
          {
            id: 1,
            name: CONNECTOR_PROVIDER_INFO[TOOLKIT_PROVIDERS.GOOGLE_SLIDES].name,
            value: TOOLKIT_PROVIDERS.GOOGLE_SLIDES,
            description: 'Create presentations with Google Slides',
            imgSvgPath: CONNECTOR_PROVIDER_INFO[TOOLKIT_PROVIDERS.GOOGLE_SLIDES].icon,
            route: '',
            connected: false
          }
        ],
        showChevron: true
      },
      {
        key: INTEGRATION_TYPE.TASKS_INTEGRATION_SETTINGS,
        title: 'Tasks',
        hasToggle: false,
        selectedKey: null,
        items: [
          {
            id: 1,
            name: CONNECTOR_PROVIDER_INFO[TOOLKIT_PROVIDERS.GOOGLE_TASKS].name,
            value: TOOLKIT_PROVIDERS.GOOGLE_TASKS,
            description: 'Manage your tasks with Google Tasks',
            imgSvgPath: CONNECTOR_PROVIDER_INFO[TOOLKIT_PROVIDERS.GOOGLE_TASKS].icon,
            route: '',
            connected: false
          }
        ],
        showChevron: true
      },
      {
        key: INTEGRATION_TYPE.STORAGE_INTEGRATION_SETTINGS,
        title: 'Storage',
        hasToggle: false,
        selectedKey: null,
        items: [
          {
            id: 1,
            name: CONNECTOR_PROVIDER_INFO[TOOLKIT_PROVIDERS.GOOGLE_DRIVE].name,
            value: TOOLKIT_PROVIDERS.GOOGLE_DRIVE,
            description: 'Store and access files with Google Drive',
            imgSvgPath: CONNECTOR_PROVIDER_INFO[TOOLKIT_PROVIDERS.GOOGLE_DRIVE].icon,
            route: '',
            connected: false
          },
          {
            id: 2,
            name: CONNECTOR_PROVIDER_INFO[TOOLKIT_PROVIDERS.SHAREPOINT].name,
            value: TOOLKIT_PROVIDERS.SHAREPOINT,
            description: 'Store and access files with Sharepoint',
            imgSvgPath: CONNECTOR_PROVIDER_INFO[TOOLKIT_PROVIDERS.SHAREPOINT].icon,
            route: '',
            connected: false
          },
          {
            id: 3,
            name: CONNECTOR_PROVIDER_INFO[TOOLKIT_PROVIDERS.ONE_DRIVE].name,
            value: TOOLKIT_PROVIDERS.ONE_DRIVE,
            description: 'Store and access files with One Drive',
            imgSvgPath: CONNECTOR_PROVIDER_INFO[TOOLKIT_PROVIDERS.ONE_DRIVE].icon,
            route: '',
            connected: false
          },
          {
            id: 4,
            name: CONNECTOR_PROVIDER_INFO[TOOLKIT_PROVIDERS.DROPBOX].name,
            value: TOOLKIT_PROVIDERS.DROPBOX,
            description: 'Store and access files with Dropbox',
            imgSvgPath: CONNECTOR_PROVIDER_INFO[TOOLKIT_PROVIDERS.DROPBOX].icon,
            route: '',
            connected: false
          }
        ],
        showChevron: true
      },
      {
        key: INTEGRATION_TYPE.DATABASE_INTEGRATION_SETTINGS,
        title: 'Databases',
        hasToggle: false,
        selectedKey: null,
        isAdminOnly: true,
        items: [
          {
            id: 1,
            name: CONNECTOR_PROVIDER_INFO[DATABASE_PROVIDERS.POSTGRES].name,
            value: DATABASE_PROVIDERS.POSTGRES,
            description: 'Store and access files with PostgreSQL',
            imgSvgPath: CONNECTOR_PROVIDER_INFO[DATABASE_PROVIDERS.POSTGRES].icon,
            route: '',
            connected: false,
            data: null
          },
          {
            id: 2,
            name: CONNECTOR_PROVIDER_INFO[DATABASE_PROVIDERS.MYSQL].name,
            value: DATABASE_PROVIDERS.MYSQL,
            description: 'Store and access files with MySQL',
            imgSvgPath: CONNECTOR_PROVIDER_INFO[DATABASE_PROVIDERS.MYSQL].icon,
            route: '',
            connected: false,
            data: null
          },
          {
            id: 3,
            name: CONNECTOR_PROVIDER_INFO[DATABASE_PROVIDERS.SQLITE].name,
            value: DATABASE_PROVIDERS.SQLITE,
            description: 'Store and access files with SQLite',
            imgSvgPath: CONNECTOR_PROVIDER_INFO[DATABASE_PROVIDERS.SQLITE].icon,
            route: '',
            connected: false,
            data: null
          },
          {
            id: 4,
            name: CONNECTOR_PROVIDER_INFO[DATABASE_PROVIDERS.MONGODB].name,
            value: DATABASE_PROVIDERS.MONGODB,
            description: 'Store and access files with MongoDB',
            imgSvgPath: CONNECTOR_PROVIDER_INFO[DATABASE_PROVIDERS.MONGODB].icon,
            route: '',
            connected: false,
            data: null
          }
        ],
        showChevron: true
      },
      {
        key: INTEGRATION_TYPE.BROWSER_INTEGRATION_SETTINGS,
        title: 'Browser',
        hasToggle: false,
        selectedKey: null,
        items: [
          {
            id: 1,
            name: CONNECTOR_PROVIDER_INFO[MISC_CONNECTORS.MY_BROWSER].name,
            value: MISC_CONNECTORS.MY_BROWSER,
            description: 'Install the Bob browser extension to help your agent access sites that require logins or have heightened security.',
            imgSvgPath: CONNECTOR_PROVIDER_INFO[MISC_CONNECTORS.MY_BROWSER].icon,
            route: '',
            connected: false
          },
        ],
        showChevron: true
      },
    ]


  public get filteredCategories() {
    return this.integrationCategories
      .filter(category => {
        const filterOption = this.filterOptions.find(f => f.value === category.key);
        return filterOption?.checked;
      })
      .map(category => ({
        ...category,
        items: category.items.filter((item: any) =>
          item.name.toLowerCase().includes(this.searchText.toLowerCase())
        )
      }))
      .filter(category => category.items.length > 0);
  }


  getSelectedValue(category: any): any {
    if (!category.selectedKey) return null;
    return (this as any)[category.selectedKey];
  }

  isItemConnected(category: any, item: any): boolean {
    return item.connected || false;
  }

  getIntegrationName(value: string): string {
    for (const category of this.integrationCategories) {
      const item = category.items.find((i: any) => i.value === value);
      if (item) {
        return item.name;
      }
    }
    return value;
  }

  onToggleChange(category: any, itemValue: string, isChecked: boolean) {
    if (category.autoToggle) {
      this.handleAutoToggle(category, itemValue, isChecked);
    }

    switch (category.key) {
      case INTEGRATION_TYPE.SIGNATORY_SETTINGS:
        this.setDefaultSignatory(itemValue as SIGNATORY_TYPE, isChecked);
        break;
      case INTEGRATION_TYPE.DOCUMENT_PROCESSING_SETTINGS:
        this.setDefaultDocumentProcessing(itemValue, isChecked);
        break;
    }
  }

  handleAutoToggle(category: any, itemValue: string, isChecked: boolean) {
    if (!isChecked) {
      const currentItem = category.items.find((item: any) => item.value === itemValue);
      const fallbackItem = category.items.find((item: any) => item.value !== itemValue);

      if (fallbackItem) {
        category.items.forEach((item: any) => {
          item.connected = item.value === fallbackItem.value;
        });

        const itemName = currentItem?.name || itemValue;
        const fallbackName = fallbackItem.name;
        this.toast.success(`${category.title} switched from ${itemName} to ${fallbackName}!`);
      }
    } else {
      category.items.forEach((item: any) => {
        item.connected = item.value === itemValue;
      });

      const selectedItem = category.items.find((item: any) => item.value === itemValue);
      const itemName = selectedItem?.name || itemValue;
      // this.toast.success(`${itemName} is now active for ${category.title}!`);
    }
  }

  ngOnInit() {
    const isDocu = this.router.url.includes('docu-sign')
    const isAdobe = this.router.url.includes('adobe-sign')
    const isMicrosoftAzure = this.router.url.includes('microsoft-azure')

    if (isDocu) {
      this.onIntegrationClick(SIGNATORY_TYPE.DOCU_SIGN, INTEGRATION_TYPE.SIGNATORY_SETTINGS)
    }
    if (isAdobe) {
      this.onIntegrationClick(SIGNATORY_TYPE.ADOBE_SIGN, INTEGRATION_TYPE.SIGNATORY_SETTINGS)
    }
    if (isMicrosoftAzure) {
      this.onIntegrationClick('MICROSOFT_AZURE', INTEGRATION_TYPE.SSO_SETTINGS)
    }

    // this.checkMicrosoftAzureConnectionStatus();
    this.getAllIntegrations();
  }

  /**
   * Check Microsoft Azure connection status
   * Queries the backend to see if organization has granted consent
   */
  checkMicrosoftAzureConnectionStatus() {
    this.microsoftAzureService.getAzureUsers().subscribe({
      next: (res) => {
        if (res.success && res.data) {
          const ssoCategory = this.integrationCategories.find(c => c.key === INTEGRATION_TYPE.SSO_SETTINGS);
          if (ssoCategory) {
            const msAzureIntegration = ssoCategory.items.find(i => i.value === 'MICROSOFT_AZURE');
            if (msAzureIntegration) {
              msAzureIntegration.connected = res.data.disconnected ? false : true;
            }
          }
        }
      },
      error: () => {
        const ssoCategory = this.integrationCategories.find(c => c.key === INTEGRATION_TYPE.SSO_SETTINGS);
        if (ssoCategory) {
          const msAzureIntegration = ssoCategory.items.find(i => i.value === 'MICROSOFT_AZURE');
          if (msAzureIntegration) {
            msAzureIntegration.connected = false;
          }
        }
      }
    });
  }


  onIntegrationClick(value: SIGNATORY_TYPE | string, key: string) {

    this.currentIntegrationType = value;

    const category = this.integrationCategories.find(c => c.key === key);

    const item = category?.items.find((i: any) => i.value === value);
    if (item?.comingSoon) {
      this.toast.info(`${item.name} Integration is coming soon!`);
      return;
    }


    switch (value) {
      case 'MISTRAL':
        if (category?.autoToggle) {
          this.handleAutoToggle(category, 'MISTRAL', true);
        }
        this.setDefaultDocumentProcessing('MISTRAL', true);
        return;

      case 'DEFAULT':
        if (category?.autoToggle) {
          this.handleAutoToggle(category, 'DEFAULT', true);
        }
        this.setDefaultDocumentProcessing('DEFAULT', true);
        return;
    }

    for (const category of this.integrationCategories) {
      const item = category.items.find((i: any) => i.value === value);
      if (item) {
        this.currentIntegrationData = {
          ...item,
          key: key,
          value: value,
        }
        break;
      }
    }

    const config = CONNECTOR_PROVIDER_INFO[value as CONNECTOR_PROVIDERS] || {
      title: `${this.getIntegrationName(value)} Integration`,
      width: '36rem',
      height: '40rem'
    };

    this.integrationDialogConfig = {
      key: key,
      value: value,
      title: config.name,
      width: '36rem',
      height: key === INTEGRATION_TYPE.SIGNATORY_SETTINGS || key == INTEGRATION_TYPE.DATABASE_INTEGRATION_SETTINGS ? '40rem' : '32rem'
    };
    this.showIntegrationDialog = true;
  }

  onDialogClose(result?: { connected?: boolean; statusChanged?: boolean }) {
    if (result) {
      if (result.statusChanged !== undefined && this.currentIntegrationType) {
        const connected = result.connected || false;

        for (const category of this.integrationCategories) {
          const item = category.items.find((i: any) => i.value === this.currentIntegrationType);
          if (item) {
            item.connected = connected;
            if (!connected) {
              item.data = undefined;
            }
            break;
          }
        }
      }

      this.showIntegrationDialog = false;
      this.currentIntegrationType = null;
      this.currentIntegrationData = null;
      this.getAllIntegrations();
      this.router.navigate(['/settings/integrations']);
    }
  }

  getDefaultSignatory() {
    this.selectedSignatory = this.integrationCategories.find(c => c.key === INTEGRATION_TYPE.SIGNATORY_SETTINGS)?.items.find(i => i.connected)?.value as SIGNATORY_TYPE;
  }

  getDefaultDocumentProcessing() {
    this.selectedDocumentProcessing = this.integrationCategories.find(c => c.key === INTEGRATION_TYPE.DOCUMENT_PROCESSING_SETTINGS)?.items.find(i => i.connected)?.value as string;
  }

  setDefaultSignatory(value: SIGNATORY_TYPE, isChecked: boolean) {
    if (!isChecked) return;

    this.selectedSignatory = value;
    this.contractSignatoryService.setDefaultSignatory(this.selectedSignatory).subscribe((res) => {
      if (res.success) {
        this.toast.success('Signatory updated successfully');
      }
    });
  }

  setDefaultDocumentProcessing(value: string, isChecked: boolean) {
    const docProcessingCategory = this.integrationCategories.find(c => c.key === INTEGRATION_TYPE.DOCUMENT_PROCESSING_SETTINGS);
    if (!docProcessingCategory) return;

    const selectedValue = isChecked ? value : docProcessingCategory.items.find(i => i.value !== value)?.value;
    if (!selectedValue) return;

    if (this.selectedDocumentProcessing === selectedValue) return;

    this.selectedDocumentProcessing = selectedValue;
    this.contractSignatoryService.setDefaultDocumentProcessing(selectedValue).subscribe(res => {
      if (res.success) {
        this.toast.success('Document processing updated successfully');
      }
    });
  }

  toggleIntegration(item: any, integration: any, connected: boolean) {
    item.connected = connected;

    if (integration.type === INTEGRATION_TYPE.DATABASE_INTEGRATION_SETTINGS && integration.data && integration.data.length > 0) {
      const dbData = integration.data.find((d: any) => d.provider === item.value);
      if (dbData) {
        item.data = {
          connectionUrl: dbData.connectionUrl || '',
          host: dbData.host,
          port: dbData.port,
          database: dbData.database,
          username: dbData.username,
          password: dbData.password
        };
      }
    }

    this.integrationCategories = this.integrationCategories.map(i => {
      if (i.key === integration.type) {
        return {
          ...i,
          items: i.items.map(j => {
            if (j.value === item.value) {
              return {
                ...j,
                connected: connected,
                data: item.data
              };
            }
            return j;
          })
        }
      }
      return i;
    });
  }


  getAllIntegrations() {
    this.isLoading = true;
    this.isAdmin = [USER_ROLE.ADMIN, USER_ROLE.SUPER_ADMIN, SHARE_ACCESS.ORG_ADMIN].includes(this.currentUser.role);
    this.integrationCategories = this.integrationCategories.filter(i => !i.isAdminOnly || (i.isAdminOnly && this.isAdmin));

    this.integrationService.getAllIntegrations().subscribe((res: any) => {
      if (res.success) {
        const integrations = Array.isArray(res.data)
          ? res.data
          : (res.data?.integrations || res.data || []);
        for (const integration of integrations) {
          if (integration.level == 'ORGANIZATION' && !this.isAdmin) {
            this.integrationCategories = this.integrationCategories.filter(i => i.key !== integration.type);
            continue;
          }

          // For SIGNATORY_SETTINGS, check if values array has any elements
          if (integration.type === INTEGRATION_TYPE.SIGNATORY_SETTINGS) {
            this.integrationCategories = this.integrationCategories.map(i => {
              if (i.key === integration.type) {
                if (integration.values && Array.isArray(integration.values) && integration.values.length > 0) {
                  const valuesAsStrings = integration.values.map((v: any) => String(v));
                  return {
                    ...i,
                    items: i.items.map(j => {
                      const itemValueAsString = String(j.value);
                      const isConnected = valuesAsStrings.includes(itemValueAsString);
                      return {
                        ...j,
                        connected: isConnected
                      };
                    })
                  };
                } else {
                  // If values array is empty or doesn't exist, mark all signatory items as disconnected
                  return {
                    ...i,
                    items: i.items.map(j => ({
                      ...j,
                      connected: false
                    }))
                  };
                }
              }
              return i;
            });
            continue;
          }

          if (integration.type === INTEGRATION_TYPE.DATABASE_INTEGRATION_SETTINGS) {
            this.integrationCategories = this.integrationCategories.map(i => {
              if (i.key === integration.type) {
                const dataList = integration.data && Array.isArray(integration.data) ? integration.data : [];
                const connectedList = (integration.values || integration.connected || []).map((v: any) => String(v).toLowerCase());
                return {
                  ...i,
                  items: i.items.map((j: any) => {
                    const dbData = dataList.find((d: any) => String(d?.provider || '').toLowerCase() === String(j.value || '').toLowerCase());
                    const isConnected = connectedList.includes(String(j.value || '').toLowerCase());
                    const itemData = dbData ? {
                      connectionUrl: dbData.connectionUrl || '',
                      host: dbData.host,
                      port: dbData.port,
                      database: dbData.database,
                      username: dbData.username,
                      password: dbData.password
                    } : null;
                    return {
                      ...j,
                      connected: isConnected,
                      data: itemData
                    };
                  })
                };
              }
              return i;
            });
            continue;
          }

          const connectedItems = integration.type == INTEGRATION_TYPE.DOCUMENT_PROCESSING_SETTINGS ? integration.values : integration.connected;

          if (connectedItems?.length) {
            const categories = this.integrationCategories.find(i => i.key === integration.type);
            if (categories && categories.items?.length) {
              const items = categories.items.map((item: any) => item.value);
              const disconnectedItems = items.filter((item: any) => !connectedItems.includes(item));
              for (const item of categories.items.filter((item: any) => disconnectedItems.includes(item.value))) {
                this.toggleIntegration(item, integration, false);
              }

              const connectedIntegrationItems = items.filter((item: any) => connectedItems.includes(item));
              for (const item of categories.items.filter((item: any) => connectedIntegrationItems.includes(item.value))) {
                this.toggleIntegration(item, integration, true);
              }
            }
          } else {
            const category = this.integrationCategories.find(i => i.key === integration.type);
            if (category) {

              for (const item of category.items) {
                if (item.connected) {
                  this.toggleIntegration(item, integration, false);
                }
              }

            }
          }
        }
        this.getDefaultSignatory();
        this.getDefaultDocumentProcessing();
      }
      this.isLoading = false;
    });
  }
}
